import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {
  Box,
  CircularProgress,
  Typography
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setSortBy } from '../ducks/slice';
import { onboardingType } from 'utility/constants';
import { formatDateTime } from 'utility/data-formatters';
import { OnboardingRequestItem } from 'utility/types';
import {
  StyledTableCell,
  StyledTableRow,
  StyledTableSortLabel
} from '../style';
import { STATUS_LABELS, RequestStatus, TableColumn, SortableColumn } from '../constants';

interface RequestsTableProps {
  requestList: OnboardingRequestItem[];
  isLoading: boolean;
  currentPage: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
}

export default function RequestsTable({
  requestList,
  isLoading,
  currentPage,
  sortBy,
  sortOrder
}: RequestsTableProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const getStatusLabel = (status: string): string => {
    const statusKey = status.toLowerCase() as RequestStatus;
    return STATUS_LABELS[statusKey] || status;
  };

  const handleSort = (column: SortableColumn) => {
    dispatch(setSortBy(column));
  };

  const getSortDirection = (column: SortableColumn): "asc" | "desc" | undefined => {
    return sortBy === column ? sortOrder : undefined;
  };

  const onClickRequestRow = (requestNumber: string) => {
    return () => {
      navigate(`/track-requests/${requestNumber}`);
    };
  };

  const onClickRequestNumber = (event: React.MouseEvent<HTMLTableCellElement>) => {
    event.stopPropagation();
  };

  const rows = requestList.map((requestItem) => ({
    requestId: requestItem.change_request_id,
    description: requestItem.description,
    requestedAt: requestItem.created_at,
    onabordingType: onboardingType[requestItem.onboarding_type as keyof typeof onboardingType] || requestItem.onboarding_type,
    status: requestItem.status,
    changeRequestId: requestItem.change_request_id,
    requestNumber: requestItem.request_number,
    raisedBy: requestItem.opened_by,
    raisedFor: requestItem.user_email
  }));

  return (
    <TableContainer sx={{
      background: "white",
      height: "55vh",
      overflow: "auto",
      width: "100%",
      minWidth: "100px",
      "& .MuiTableCell-head": {
        background: "#11192A",
        padding: "7px 16px",
        color: "white",
      },
      "& .MuiTableRow-root:nth-of-type(odd)": {
        backgroundColor: "#EBF2FE"
      },
      "& .MuiTableCell-root.MuiTableCell-body": {
        padding: "3px 16px",
        color: "black"
      }
    }}>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Table aria-label="customized table" stickyHeader>
          <TableHead>
            <TableRow>
              <StyledTableCell align="left">{TableColumn.SERIAL_NUMBER}</StyledTableCell>
              <StyledTableCell align="left">{TableColumn.REQUEST_NUMBER}</StyledTableCell>
              <StyledTableCell align="left">{TableColumn.RAISED_BY}</StyledTableCell>
              <StyledTableCell align="left">{TableColumn.RAISED_FOR}</StyledTableCell>
              <StyledTableCell align="left">{TableColumn.DESCRIPTION}</StyledTableCell>
              <StyledTableCell align="left">
                <StyledTableSortLabel
                  active={sortBy === SortableColumn.CREATED_AT}
                  direction={getSortDirection(SortableColumn.CREATED_AT)}
                  onClick={() => handleSort(SortableColumn.CREATED_AT)}
                >
                  {TableColumn.REQUESTED_AT}
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="left">{TableColumn.ONBOARDING_TYPE}</StyledTableCell>
              <StyledTableCell align="left">
                <StyledTableSortLabel
                  active={sortBy === SortableColumn.STATUS}
                  direction={getSortDirection(SortableColumn.STATUS)}
                  onClick={() => handleSort(SortableColumn.STATUS)}
                >
                  {TableColumn.STATUS}
                </StyledTableSortLabel>
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <StyledTableCell colSpan={8} align="center">
                  <Typography variant="body1" sx={{ py: 4 }}>
                    No requests found
                  </Typography>
                </StyledTableCell>
              </TableRow>
            ) : (
              rows.map((row, rowIndex) => (
                <StyledTableRow key={rowIndex + 1} onClick={onClickRequestRow(row.requestNumber)}>
                  <StyledTableCell align="left">{(currentPage - 1) * 20 + rowIndex + 1}</StyledTableCell>
                  <StyledTableCell align="left" onClick={onClickRequestNumber}>
                    <Link to={`/track-requests/${row.requestNumber}`}>{row.requestNumber}</Link>
                  </StyledTableCell>
                  <StyledTableCell align="left">{row.raisedBy}</StyledTableCell>
                  <StyledTableCell align="left">{row.raisedFor}</StyledTableCell>
                  <StyledTableCell align="left">{row.description}</StyledTableCell>
                  <StyledTableCell align="left">{formatDateTime(row.requestedAt)}</StyledTableCell>
                  <StyledTableCell align="left">{row.onabordingType}</StyledTableCell>
                  <StyledTableCell align="left">{getStatusLabel(row.status)}</StyledTableCell>
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );
}
